import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Yeni sürüm yayına çıkınca açık sekmeyi kendiliğinden günceller (kullanıcı F5'e basmadan).
 *
 * Çalışan kodun kimliği (REACT_APP_BUILD_ID, scripts/build.js derlemede gömer) ile yayındaki
 * /version.json karşılaştırılır. Farklıysa yeni sürüm var demektir. Yenileme kullanıcıyı
 * rahatsız etmeyecek ilk anda yapılır:
 *  - sekme arka plandaysa hemen,
 *  - kullanıcı başka sayfaya geçerse o geçişte,
 *  - ekranda çalışıyorsa kısa bir geri sayımla — ama bir alana yazıyorsa ya da açık bir
 *    popup varsa yarım iş kaybolmasın diye bitirene kadar beklenir.
 *
 * Geliştirme ortamında (npm start) kimlik olmadığı için kapalıdır.
 */

const BUILD_ID = process.env.REACT_APP_BUILD_ID;
const CHECK_EVERY_MS = 2 * 60 * 1000;
const FIRST_CHECK_MS = 20 * 1000;
const COUNTDOWN_S = 30;
const RELOADED_FOR_KEY = "reloadedForBuild";

async function fetchDeployedBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`${process.env.PUBLIC_URL}/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.buildId === "string" ? data.buildId : null;
  } catch {
    return null; // çevrimdışı, eski yayında dosya yok vb. — sessizce sonra tekrar denenir
  }
}

/** Kullanıcı şu an bir şey yazıyor ya da bir popup açık mı — öyleyse yenileme bekler. */
function userIsBusy(): boolean {
  const el = document.activeElement as HTMLElement | null;
  const typing = !!el && (
    el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable
  );
  const modalOpen = !!document.querySelector(
    ".modal.show, [role='dialog'], .dinein-modal-backdrop, .roles__backdrop"
  );
  return typing || modalOpen;
}

export default function UpdateWatcher() {
  const location = useLocation();
  const [newBuildId, setNewBuildId] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_S);
  const [snoozedUntil, setSnoozedUntil] = useState(0);
  const [busy, setBusy] = useState(false);
  const firstPath = useRef(location.pathname);

  const reloadNow = useCallback((target?: string) => {
    if (newBuildId) {
      // Önbellek eski index.html verirse aynı sürüm için tekrar tekrar yenilenmesin.
      try { sessionStorage.setItem(RELOADED_FOR_KEY, newBuildId); } catch {}
    }
    if (target) window.location.assign(target);
    else window.location.reload();
  }, [newBuildId]);

  const check = useCallback(async () => {
    if (!BUILD_ID) return;
    const deployed = await fetchDeployedBuildId();
    if (!deployed || deployed === BUILD_ID) return;
    let alreadyTried = false;
    try { alreadyTried = sessionStorage.getItem(RELOADED_FOR_KEY) === deployed; } catch {}
    if (alreadyTried) return; // bu sürüm için bir kez yenilendik; sunucu hâlâ eskiyi veriyor
    setNewBuildId(deployed);
  }, []);

  // Periyodik kontrol + sekmeye dönünce + internet gelince
  useEffect(() => {
    if (!BUILD_ID) return;
    console.info(`[Sürüm] Çalışan: ${BUILD_ID}`);
    const first = setTimeout(check, FIRST_CHECK_MS);
    const timer = setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", check);
    window.addEventListener("focus", check);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", check);
      window.removeEventListener("focus", check);
    };
  }, [check]);

  // Yeni sürüm varken sekme arka plana geçerse hemen yenile (kullanıcı görmez)
  useEffect(() => {
    if (!newBuildId) return;
    if (document.visibilityState === "hidden") { reloadNow(); return; }
    const onHidden = () => { if (document.visibilityState === "hidden") reloadNow(); };
    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
  }, [newBuildId, reloadNow]);

  // Yeni sürüm varken sayfa değiştirilirse: gidilen sayfayı yeni sürümle aç
  useEffect(() => {
    if (!newBuildId) { firstPath.current = location.pathname; return; }
    if (location.pathname !== firstPath.current) {
      reloadNow(window.location.pathname + window.location.search + window.location.hash);
    }
  }, [location.pathname, newBuildId, reloadNow]);

  // Ekranda çalışıyorsa geri sayım; yazı yazıyorsa ya da popup açıksa beklet
  useEffect(() => {
    if (!newBuildId) return;
    const tick = setInterval(() => {
      if (Date.now() < snoozedUntil) return;
      const nowBusy = userIsBusy();
      setBusy(nowBusy);
      if (nowBusy) return; // bitirene kadar bekle, sayaç da durur
      setSecondsLeft((s) => {
        if (s <= 1) { reloadNow(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [newBuildId, snoozedUntil, reloadNow]);

  if (!newBuildId) return null;
  const snoozed = Date.now() < snoozedUntil;
  if (snoozed) return null;

  return (
    <div className="upd-banner" role="status" aria-live="polite">
      <i className="bi bi-arrow-repeat upd-banner__icon"></i>
      <span className="upd-banner__text">
        Panelin yeni sürümü yayında. {busy
          ? "İşinizi bitirince sayfa kendiliğinden yenilenecek."
          : <>Sayfa <strong>{secondsLeft} sn</strong> içinde yenilenecek.</>}
      </span>
      <button type="button" className="upd-banner__btn" onClick={() => reloadNow()}>Şimdi yenile</button>
      <button type="button" className="upd-banner__later" onClick={() => { setSnoozedUntil(Date.now() + 5 * 60 * 1000); setSecondsLeft(COUNTDOWN_S); }}>
        5 dk sonra
      </button>
    </div>
  );
}
